import type { ButtonHTMLAttributes } from "react";

import { cn } from "../cn";

type Variant = "primary" | "secondary" | "ghost";
/** `sm` is the dense size the console uses in tables, headers and rows; `md` is
    the default for standalone actions. Screens were hand-rolling the small one
    with literal heights and paddings, which is how a button ends up looking
    slightly different on every screen. */
type Size = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const SIZES: Record<Size, string> = {
  sm: "h-[34px] gap-[7px] rounded-lg px-[14px] text-[13px] font-semibold",
  md: "gap-2 rounded-[10px] px-4 py-2.5 text-sm",
};

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-acc text-acc-ink hover:bg-acc-hover font-semibold",
  secondary:
    "bg-surface-2 text-ink border border-line-strong hover:bg-surface-3",
  ghost: "text-dim hover:text-ink hover:bg-surface-2",
};

/** Product-agnostic button. Yellow accent is the primary call to action. */
export function Button({
  variant = "primary",
  size = "md",
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap",
        "transition-colors disabled:pointer-events-none disabled:opacity-50",
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
}
